/**
 * Utility functions for handling API errors and converting status codes to user-friendly messages
 */

/**
 * Maps HTTP status codes to user-friendly error messages
 * @param {number} statusCode - HTTP status code
 * @param {string} context - Optional context for more specific messages
 * @param {string} originalMessage - Original error message from API
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (statusCode, context = '', originalMessage = '') => {
  // If we have a meaningful error message from the API, use it
  if (originalMessage && 
      originalMessage.length > 0 && 
      !originalMessage.includes('Internal Server Error') &&
      !originalMessage.includes('Bad Request') &&
      !originalMessage.includes('Unauthorized') &&
      !originalMessage.includes('Forbidden') &&
      !originalMessage.includes('Not Found')) {
    return originalMessage;
  }
  const contextMessages = {
    'login': {
      400: 'Please check your username and password.',
      401: 'Invalid username or password. Please try again.',
      403: 'Your account has been temporarily locked. Please contact support.',
      429: 'Too many login attempts. Please try again in a few minutes.',
      500: 'Unable to process login at the moment. Please try again later.',
      502: 'Login service is temporarily unavailable. Please try again later.',
      503: 'Login service is under maintenance. Please try again later.',
      default: 'Unable to sign in. Please try again.'
    },
    'reservation': {
      400: 'Invalid reservation details. Please check your information.',
      401: 'Please log in to manage reservations.',
      403: 'You don\'t have permission to access this reservation.',
      404: 'Reservation not found. It may have been cancelled or modified.',
      409: 'This reservation conflicts with existing bookings.',
      422: 'Reservation dates are no longer available.',
      429: 'Too many requests. Please wait a moment before trying again.',
      500: 'Unable to process reservation. Please try again later.',
      502: 'Reservation service is temporarily unavailable.',
      503: 'Reservation system is under maintenance.',
      default: 'Unable to process reservation. Please try again.'
    },
    'room': {
      400: 'Invalid room information provided.',
      401: 'Please log in to access room information.',
      403: 'You don\'t have permission to manage rooms.',
      404: 'Room not found or no longer available.',
      409: 'Room is already occupied or blocked.',
      422: 'Room cannot be modified due to active reservations.',
      500: 'Unable to access room information. Please try again later.',
      502: 'Room management service is temporarily unavailable.',
      503: 'Room system is under maintenance.',
      default: 'Unable to access room information. Please try again.'
    },
    'payment': {
      400: 'Invalid payment information. Please check your details.',
      401: 'Please log in to process payments.',
      402: 'Payment required to complete this action.',
      403: 'Payment processing is not authorized for your account.',
      404: 'Payment record not found.',
      409: 'Payment has already been processed.',
      422: 'Payment method declined. Please try a different payment method.',
      429: 'Too many payment attempts. Please wait before trying again.',
      500: 'Payment processing is temporarily unavailable.',
      502: 'Payment gateway is currently unavailable.',
      503: 'Payment system is under maintenance.',
      default: 'Unable to process payment. Please try again.'
    },
    'reports': {
      400: 'Invalid report parameters. Please check your selections.',
      401: 'Please log in to access reports.',
      403: 'You don\'t have permission to view this report.',
      404: 'Report data not available for the selected period.',
      408: 'Report generation timed out. Please try with a smaller date range.',
      429: 'Too many report requests. Please wait before generating another report.',
      500: 'Unable to generate report. Please try again later.',
      502: 'Reporting service is temporarily unavailable.',
      503: 'Reporting system is under maintenance.',
      default: 'Unable to generate report. Please try again.'
    },
    'upload': {
      400: 'Invalid file format or size. Please check file requirements.',
      401: 'Please log in to upload files.',
      403: 'You don\'t have permission to upload files.',
      413: 'File is too large. Please choose a smaller file.',
      415: 'File type not supported. Please use a different format.',
      422: 'File validation failed. Please check the file content.',
      429: 'Too many upload attempts. Please wait before trying again.',
      500: 'File upload failed. Please try again later.',
      502: 'Upload service is temporarily unavailable.',
      503: 'Upload system is under maintenance.',
      default: 'Unable to upload file. Please try again.'
    }
  };

  const generalMessages = {
    400: 'Invalid request. Please check your information and try again.',
    401: 'Please log in to continue.',
    403: 'You don\'t have permission to perform this action.',
    404: 'The requested information could not be found.',
    405: 'This action is not allowed.',
    408: 'Request timed out. Please try again.',
    409: 'This action conflicts with existing data.',
    422: 'The submitted information could not be processed.',
    429: 'Too many requests. Please wait a moment before trying again.',
    500: 'Something went wrong on our end. Please try again later.',
    502: 'Service is temporarily unavailable. Please try again later.',
    503: 'System is under maintenance. Please try again later.',
    504: 'Request timed out. Please try again.',
    default: 'An unexpected error occurred. Please try again.'
  };

  // Get context-specific messages if available
  const contextMap = contextMessages[context?.toLowerCase()];
  if (contextMap) {
    return contextMap[statusCode] || contextMap.default;
  }

  // Fall back to general messages
  return generalMessages[statusCode] || generalMessages.default;
};

/**
 * Formats error for display to user
 * @param {Object} error - Error object from API
 * @param {string} context - Optional context for more specific messages
 * @returns {Object} Formatted error with user-friendly message
 */
export const formatErrorForUser = (error, context = '') => {
  let statusCode = 500;
  let originalMessage = 'An unexpected error occurred';

  // Extract status code from various error formats
  if (error?.response?.status) {
    statusCode = error.response.status;
  } else if (error?.status) {
    statusCode = error.status;
  } else if (typeof error === 'number') {
    statusCode = error;
  }

  // Extract original message
  if (error?.response?.data?.message) {
    originalMessage = error.response.data.message;
  } else if (error?.response?.data?.detail) {
    originalMessage = error.response.data.detail;
  } else if (error?.message) {
    originalMessage = error.message;
  } else if (error?.detail) {
    originalMessage = error.detail;
  }

  return {
    userMessage: getErrorMessage(statusCode, context, originalMessage),
    originalMessage,
    statusCode,
    timestamp: new Date().toISOString()
  };
};

/**
 * Shows user-friendly toast notification for errors
 * @param {Function} toast - Chakra UI toast function
 * @param {Object} error - Error object from API
 * @param {string} context - Optional context for more specific messages
 * @param {string} title - Optional custom title
 */
export const showErrorToast = (toast, error, context = '', title = 'Error') => {
  const formattedError = formatErrorForUser(error, context);
  
  toast({
    title,
    description: formattedError.userMessage,
    status: 'error',
    duration: 5000,
    isClosable: true,
  });

  // Log detailed error for debugging
  console.error(`${title} (${context}):`, {
    userMessage: formattedError.userMessage,
    originalMessage: formattedError.originalMessage,
    statusCode: formattedError.statusCode,
    fullError: error
  });
};

/**
 * Gets user-friendly message for network connectivity issues
 * @param {Object} error - Error object
 * @returns {string} User-friendly message
 */
export const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return 'You appear to be offline. Please check your internet connection.';
  }

  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }

  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  return 'Connection error. Please check your internet connection and try again.';
};

/**
 * Determines if an error is a network/connectivity issue
 * @param {Object} error - Error object
 * @returns {boolean} True if it's a network error
 */
export const isNetworkError = (error) => {
  return !error?.response && (
    error?.code === 'NETWORK_ERROR' ||
    error?.message?.includes('Network Error') ||
    error?.code === 'ECONNABORTED' ||
    !navigator.onLine
  );
};